import { Component, ElementRef, viewChild } from '@angular/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';

interface NumericCellEditorParams extends ICellEditorParams {
  /** Accessible name for the bare input (the column header is not auto-associated). */
  ariaLabel?: string;
}

@Component({
  standalone: true,
  selector: 'ces-numeric-cell-editor',
  templateUrl: './numeric-cell-editor.component.html',
  styles: `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    input {
      width: 100%;
      height: 100%;
      border: none;
      outline: none;
      padding: 0 11px;
      font-size: inherit;
      font-family: inherit;
      /* Follow the host cell's alignment class so edit start does not shift the value. */
      text-align: inherit;
      background: transparent;
      box-sizing: border-box;
    }
  `,
})
export class NumericCellEditorComponent implements ICellEditorAngularComp {
  private readonly inputEl = viewChild.required<ElementRef<HTMLInputElement>>('input');
  protected ariaLabel: string | null = null;
  private value = '';
  private startedByKeyPress = false;
  private cancelBeforeStart = false;

  agInit(params: NumericCellEditorParams): void {
    this.ariaLabel = params.ariaLabel ?? null;
    const keyPress = params.eventKey;
    const isValidKey = !keyPress || /^[0-9.,-]$/.test(keyPress);

    if (!isValidKey) {
      this.cancelBeforeStart = true;
      return;
    }

    if (keyPress) {
      this.value = keyPress === ',' ? '.' : keyPress;
      this.startedByKeyPress = true;
    } else {
      this.value = params.value != null ? String(params.value) : '';
    }
  }

  isCancelBeforeStart(): boolean {
    return this.cancelBeforeStart;
  }

  getValue(): number | null {
    if (!this.value || this.value === '-') return null;
    const num = parseFloat(this.value.replace(/,/g, '.'));
    if (isNaN(num)) return null;
    return num;
  }

  afterGuiAttached(): void {
    const input = this.inputEl().nativeElement;
    input.value = this.value;
    input.focus();
    if (!this.startedByKeyPress) {
      input.select();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    const key = event.key;
    if (key === 'e' || key === 'E' || key === '+') {
      event.preventDefault();
      return;
    }
    if (key === '-') {
      const input = event.target as HTMLInputElement;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const allSelected = start === 0 && end === input.value.length && input.value.length > 0;
      const canInsert = (start === 0 || allSelected) && (!input.value.includes('-') || allSelected);
      if (!canInsert) event.preventDefault();
    }
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    const len = val.length;
    const cursor = input.selectionStart ?? len;

    val = val.replace(/[^0-9.,-]/g, '').replace(/,/g, '.');

    if (val.includes('-')) {
      const digits = val.replace(/-/g, '');
      val = val.startsWith('-') ? '-' + digits : digits;
    }

    const dot = val.indexOf('.');
    if (dot !== -1) {
      // Single decimal point, precision as typed: 76.882103 must stay 76.882103.
      val = val.slice(0, dot) + '.' + val.slice(dot + 1).replace(/\./g, '');
    }

    if (val !== input.value) {
      input.value = val;
      const pos = Math.max(0, cursor - (len - val.length));
      input.setSelectionRange(pos, pos);
    }

    this.value = val;
  }
}
